import React, { createContext, useState, useContext, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ShoppingCartContext = createContext();

export const useShoppingCart = () => {
  return useContext(ShoppingCartContext);
};

export const ShoppingCartProvider = ({ children, token }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartItemCount, setCartItemCount] = useState(0);
  // ADR-027 §A1.4(a): the past-renewal Path-B booking-return context, carried
  // in-memory from the drop-in/package choice page to the cart checkout. This
  // is the ADR-027-conformant carrier for the IN-APP (same-domain SPA) leg —
  // tab-scoped by construction (React state, not any browser-storage API) and
  // self-cleaning on reload. The EXTERNAL Stripe-redirect leg is carried by
  // the backend success_url (001-F-BE). Replaces the removed legacy
  // browser-storage carrier (the R-020 paid-but-stranded vector).
  const [bookingReturn, setBookingReturn] = useState(null);

  useEffect(() => {
    setCartItemCount(cartItems.length);
  }, [cartItems]);

  const addItem = (item) => {
    // Ensure the added item conforms to the structure
    if (item.priceId && item.type) {
      // Check for duplicate intro offers before adding
      if (item.isIntroOffer) {
        const existingIntroOffer = cartItems.find(
          (cartItem) => cartItem.isIntroOffer
        );

        if (existingIntroOffer) {
          // Show error toast for duplicate intro offer
          toast.error(
            "You already have an intro offer in your cart. Each customer can only claim one.",
            {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            style: {
              background: "#fff1f0",
              color: "#cf1322",
              borderLeft: "4px solid #cf1322",
            },
            }
          );
          return; // Prevent adding duplicate intro offer
        }
      }

      // Generate a unique id if not provided
      if (!item.id) {
        item.id = Date.now() + Math.random();
      }
      setCartItems([...cartItems, item]);

      // Show toast notification
      toast.success(`${item.name} added to cart!`, {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        style: {
          background: "var(--primary)",
          color: "var(--accent1)",
          borderLeft: "4px solid var(--accent2)",
        },
      });
    } else {
      console.error("Invalid item format:", item);

      // Show error toast
      toast.error("Could not add item to cart", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        style: {
          background: "#fff1f0",
          color: "#cf1322",
          borderLeft: "4px solid #cf1322",
        },
      });
    }
  };

  const removeItem = (itemId) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
  };

  /*const clearCart = () => {
    setCartItems([]);
  };*/

  const updateItemCount = (newCount) => {
    setCartItemCount(newCount);
  };

  // New function: updateItemQuantity
  const updateItemQuantity = (itemId, newQuantity) => {
    setCartItems((prevItems) =>
      prevItems.map((item) => {
        // Check if this is an intro offer product
        const isIntroOffer = item.isIntroOffer === true;

        // If it's an intro offer, don't update the quantity
        if (item.id === itemId && isIntroOffer) {
          return item;
        }

        // Otherwise, update as normal
        return item.id === itemId ? { ...item, quantity: newQuantity } : item;
      })
    );
  };

  const value = {
    cartItems,
    addItem,
    removeItem,
    updateItemQuantity,
    cartItemCount,
    token, // shared token from App.js
    updateItemCount,
    bookingReturn, // ADR-027 §A1.4(a) — Path-B in-app return carrier
    setBookingReturn,
  };

  return (
    <ShoppingCartContext.Provider value={value}>
      {children}
    </ShoppingCartContext.Provider>
  );
};
